import Handlebars from 'handlebars';
import { deriveServicePeriodLabel } from './invoice-period';

/**
 * The body of the email an invoice is sent with.
 *
 * Invoice settings hold a Handlebars TEMPLATE for it; an invoice may carry an
 * override, which is FINISHED text (no variables) and is stored only when
 * someone edits the message on that invoice. Everything else resolves the
 * tenant's current template at send time, so an unedited invoice costs one
 * null column.
 *
 * Lives in `shared` because the settings screen previews the template, the
 * invoice editor shows it filled in, and the sender renders it — three
 * renderings that must produce the same text from the same formatting.
 *
 * Plain text, not HTML: a blank line starts a paragraph. The sender escapes it
 * into the HTML part, so nothing a user types can inject markup.
 */

/** The variables a template may use, in the order the UI lists them. */
export const INVOICE_EMAIL_VARIABLES = [
  { name: 'invoiceNumber', label: 'Invoice number', example: '26-09-0007' },
  { name: 'amount', label: 'Amount (total, with currency)', example: '$5,000.00' },
  { name: 'servicePeriod', label: 'Service period', example: 'September 2026' },
  { name: 'dueDate', label: 'Due date', example: 'Oct 10, 2026' },
  { name: 'customerName', label: 'Customer (bill-to) name', example: 'Acme Corp' },
  { name: 'issuerName', label: 'Your company name', example: 'MystartupCFO, LLC' },
] as const;

export type InvoiceEmailVariable = (typeof INVOICE_EMAIL_VARIABLES)[number]['name'];
export type InvoiceEmailValues = Record<InvoiceEmailVariable, string>;

/** Longest template or override accepted. Well past any cover note. */
export const MAX_INVOICE_EMAIL_BODY_CHARS = 5000;

/**
 * The message used when invoice settings hold no template of their own. Not
 * copied into the settings row: a tenant that never customises it stores null
 * and picks up any later change to this text.
 */
export const DEFAULT_INVOICE_EMAIL_BODY_TEMPLATE = [
  'Hello,',
  'Please find attached invoice {{invoiceNumber}} for {{amount}}{{#if servicePeriod}}, for services in {{servicePeriod}}{{/if}}.{{#if dueDate}} Payment is due {{dueDate}}.{{/if}} Payment instructions are on the invoice.',
  'Reply to this email with any questions.',
  '{{issuerName}}',
].join('\n\n');

/**
 * The subject line, as a template over the same variables as the body. Null
 * in settings means this text, for the same reason the body's default is not
 * copied into the row.
 */
export const DEFAULT_INVOICE_EMAIL_SUBJECT_TEMPLATE =
  'Invoice {{invoiceNumber}} from {{issuerName}}';

/** Longest subject template accepted. Mail clients truncate far sooner. */
export const MAX_INVOICE_EMAIL_SUBJECT_CHARS = 200;

/** Block helpers a template may use — enough to drop a sentence when a value
 *  is empty (an ad-hoc invoice has no service period), nothing that loops or
 *  reaches outside the fixed variable set. */
const BLOCK_HELPERS = new Set(['if', 'unless']);
const VARIABLE_NAMES = new Set<string>(INVOICE_EMAIL_VARIABLES.map((v) => v.name));

/** What an invoice supplies to fill a template. Loose on purpose: the sender
 *  passes a row, the editor passes form values mid-edit. */
export interface InvoiceEmailSource {
  invoiceNumber: string;
  /** Stored decimal string, e.g. `5000.00`. */
  total: string;
  currency: string;
  dueDate: string | null | undefined;
  servicePeriodStart: string | null | undefined;
  servicePeriodEnd: string | null | undefined;
  customerName: string;
  issuerName: string;
}

export function invoiceEmailValues(src: InvoiceEmailSource): InvoiceEmailValues {
  return {
    invoiceNumber: src.invoiceNumber,
    amount: formatInvoiceEmailAmount(src.total, src.currency),
    servicePeriod: deriveServicePeriodLabel(src.servicePeriodStart, src.servicePeriodEnd) ?? '',
    dueDate: src.dueDate ? formatInvoiceEmailDate(src.dueDate) : '',
    customerName: src.customerName.trim(),
    issuerName: src.issuerName.trim(),
  };
}

/** Sample values for previewing a template with no invoice at hand. */
export function sampleInvoiceEmailValues(issuerName?: string): InvoiceEmailValues {
  const values = Object.fromEntries(
    INVOICE_EMAIL_VARIABLES.map((v) => [v.name, v.example]),
  ) as InvoiceEmailValues;
  if (issuerName?.trim()) values.issuerName = issuerName.trim();
  return values;
}

/**
 * Why a template cannot be used, or null when it can.
 *
 * Refuses an unknown variable rather than rendering it blank: `{{amnt}}` would
 * otherwise send "for ." to a customer, and nobody would see it until they
 * replied. Also refuses partials, loops and every helper but `if` / `unless`.
 */
export function findInvoiceEmailTemplateError(template: string): string | null {
  let program: hbs.AST.Program;
  try {
    program = Handlebars.parse(template);
  } catch (err) {
    const first = err instanceof Error ? (err.message.split('\n')[0] ?? '') : '';
    return `The template has a syntax error${first ? ` (${first.replace(/:$/, '')})` : ''}. Check every {{ has a matching }} and every {{#if}} a {{/if}}.`;
  }
  return checkStatements(program.body);
}

/** The same check for a subject template, which must also be one line: a
 *  header cannot carry a line break. */
export function findInvoiceEmailSubjectTemplateError(template: string): string | null {
  if (/[\r\n]/.test(template)) return 'The subject must be a single line.';
  return findInvoiceEmailTemplateError(template);
}

/**
 * What settings store for an edited template: null — "use the built-in
 * default" — when it is blank or says exactly what the default says, so a
 * later change to the default reaches every tenant that never customised it.
 * Otherwise the template, trimmed.
 */
export function storedInvoiceEmailTemplate(
  value: string | null,
  defaultTemplate: string,
): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' || trimmed === defaultTemplate ? null : trimmed;
}

function checkStatements(statements: hbs.AST.Statement[]): string | null {
  for (const statement of statements) {
    const error = checkStatement(statement);
    if (error) return error;
  }
  return null;
}

function checkStatement(statement: hbs.AST.Statement): string | null {
  switch (statement.type) {
    case 'ContentStatement':
    case 'CommentStatement':
      return null;
    case 'MustacheStatement': {
      const m = statement as hbs.AST.MustacheStatement;
      if (m.params.length > 0 || m.hash) return unsupported(m.path);
      return checkVariable(m.path);
    }
    case 'BlockStatement': {
      const b = statement as hbs.AST.BlockStatement;
      const helper = pathName(b.path);
      if (helper === null || !BLOCK_HELPERS.has(helper)) {
        return `{{#${expressionText(b.path)}}} is not supported. Use {{#if name}}…{{/if}} or {{#unless name}}…{{/unless}}.`;
      }
      const [param] = b.params;
      if (b.params.length !== 1 || !param || b.hash) {
        return `{{#${helper}}} takes exactly one variable, e.g. {{#${helper} dueDate}}.`;
      }
      return (
        checkVariable(param) ??
        checkStatements(b.program?.body ?? []) ??
        checkStatements(b.inverse?.body ?? [])
      );
    }
    default:
      return 'Partials and decorators are not supported in the email template.';
  }
}

function checkVariable(expr: hbs.AST.Expression): string | null {
  const name = pathName(expr);
  if (name !== null && VARIABLE_NAMES.has(name)) return null;
  return `Unknown variable {{${expressionText(expr)}}}. Available: ${INVOICE_EMAIL_VARIABLES.map((v) => v.name).join(', ')}.`;
}

function unsupported(expr: hbs.AST.Expression): string {
  return `{{${expressionText(expr)} …}} is not supported. Use a variable on its own, e.g. {{invoiceNumber}}.`;
}

/** A plain one-segment variable name, or null for `this`, `@root.x`, `a.b`,
 *  literals and sub-expressions. */
function pathName(expr: hbs.AST.Expression): string | null {
  if (expr.type !== 'PathExpression') return null;
  const path = expr as hbs.AST.PathExpression;
  if (path.data || path.parts.length !== 1) return null;
  return path.parts[0] ?? null;
}

function expressionText(expr: hbs.AST.Expression): string {
  if (expr.type === 'PathExpression') return (expr as hbs.AST.PathExpression).original;
  const literal = expr as { original?: unknown };
  return literal.original !== undefined ? String(literal.original) : expr.type;
}

/** An isolated environment: no helper registered on the global `Handlebars`
 *  by some other module can reach a customer's email. */
const env = Handlebars.create();

/**
 * Fill a template. Throws when the template is invalid — callers validate on
 * save, so reaching this with a bad template is a bug worth surfacing rather
 * than a message to send half-filled.
 */
export function renderInvoiceEmailBody(template: string, values: InvoiceEmailValues): string {
  const error = findInvoiceEmailTemplateError(template);
  if (error) throw new Error(error);
  return env
    .compile(template, {
      // Plain text: the sender escapes the RESULT into HTML. Escaping here too
      // would put `&amp;` in the text part.
      noEscape: true,
      strict: true,
      knownHelpers: { if: true, unless: true },
      knownHelpersOnly: true,
    })(values)
    .trim();
}

/** Like {@link renderInvoiceEmailBody}, but returns null instead of throwing —
 *  for previews rendering a template that is still being typed. */
export function tryRenderInvoiceEmailBody(
  template: string,
  values: InvoiceEmailValues,
): string | null {
  try {
    return renderInvoiceEmailBody(template, values);
  } catch {
    // A half-typed template is the normal state of a preview, not a failure;
    // the form shows `findInvoiceEmailTemplateError` beside the input.
    return null;
  }
}

/**
 * Fill a subject template. One line, always: a header cannot carry a line
 * break, and a `{{#if}}` spanning two lines of the template must not put one
 * there — so every run of whitespace becomes one space. Throws on an invalid
 * template, like {@link renderInvoiceEmailBody}.
 */
export function renderInvoiceEmailSubject(template: string, values: InvoiceEmailValues): string {
  return renderInvoiceEmailBody(template, values).replace(/\s+/g, ' ').trim();
}

/** Like {@link renderInvoiceEmailSubject}, but null instead of throwing. */
export function tryRenderInvoiceEmailSubject(
  template: string,
  values: InvoiceEmailValues,
): string | null {
  try {
    return renderInvoiceEmailSubject(template, values);
  } catch {
    return null;
  }
}

/**
 * `5000.00` + `USD` → `$5,000.00`. An unparseable total prints as stored
 * rather than as an invented `$0.00`: `Number('')` is 0.
 */
export function formatInvoiceEmailAmount(total: string, currency: string): string {
  const n = Number(total);
  if (total.trim() === '' || !Number.isFinite(n)) return `${total} ${currency}`;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);
  } catch {
    // An ISO code `Intl` does not know — print the stored figure and code
    // rather than fail the send over formatting.
    return `${total} ${currency}`;
  }
}

/** `2026-10-30` → `Oct 30, 2026`, read as a calendar date (UTC), not an instant. */
export function formatInvoiceEmailDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' }).format(d);
}

/**
 * The override to store for an edited message: null when it says exactly what
 * the default would, so an operator who edits and then puts the text back
 * leaves nothing behind. Compared ignoring surrounding whitespace, which the
 * sender trims anyway.
 */
export function invoiceEmailBodyOverride(
  text: string,
  renderedDefault: string | null,
): string | null {
  const trimmed = text.trim();
  if (renderedDefault !== null && trimmed === renderedDefault.trim()) return null;
  return trimmed;
}
